import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { GridPaginationModel } from '@mui/x-data-grid-pro'
import { FeeWithdrawalDetailsModal } from 'src/components/payments-ops/fee-withdrawals/FeeWithdrawalDetailsModal'
import { FeeWithdrawalReportPreviewModal } from 'src/components/payments-ops/fee-withdrawals/FeeWithdrawalReportPreviewModal'
import { FeeWithdrawalsTable } from 'src/components/payments-ops/fee-withdrawals/FeeWithdrawalsTable'
import { FeeWithdrawalsToolbar } from 'src/components/payments-ops/fee-withdrawals/FeeWithdrawalsToolbar'
import { PageHeader } from 'src/components/payments-ops/PageHeader'
import { Button } from 'src/components/shared/Button'
import { AdminFetchFeeWithdrawals } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type {
  FeeWithdrawal,
  FeeWithdrawalFilters,
  FetchFeeWithdrawalsPayload,
} from 'src/interfaces/feeWithdrawal'
import { getFeeWithdrawalsError } from 'src/selectors/feeWithdrawals'
import { useFeeWithdrawalReport } from 'src/utils/hooks/paymentsOps/useFeeWithdrawalReport'
import { useQueryParamUpdater } from 'src/utils/hooks/paymentsOps/useQueryParamUpdater'
import {
  buildFeeWithdrawalReportFileName,
  buildFeeWithdrawalsPaginationParams,
  parseFeeWithdrawalsUrlState,
} from 'src/utils/paymentsOps/feeWithdrawals'

const FeesWithdrawReportsPageComponent = () => {
  const [searchParams] = useSearchParams()
  const updateParams = useQueryParamUpdater()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const listError = useSelector(getFeeWithdrawalsError)
  const report = useFeeWithdrawalReport()
  // The toolbar hides the partner picker without `partners_view`; a `partnerId` left in the URL
  // would then filter the list invisibly, with nothing to clear it — so it is ignored.
  const canViewPartners = useHasPermission(InternalPermission.PartnersView)

  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [detailsWithdrawal, setDetailsWithdrawal] = useState<FeeWithdrawal | null>(null)

  const urlState = useMemo(() => {
    return parseFeeWithdrawalsUrlState(searchParams)
  }, [searchParams])

  const { hasReportOnly } = urlState
  const { status, type } = urlState.filters
  const partnerId = canViewPartners ? urlState.filters.partnerId : null

  const filters = useMemo<FeeWithdrawalFilters>(() => {
    return { status, type, partnerId }
  }, [partnerId, status, type])
  const { page, pageSize } = urlState.paginationModel

  // Keyed on the numbers, not the parsed object: every URL change (e.g. the client-only
  // "Has report" box) builds a fresh object, which must not refetch the list.
  const paginationModel = useMemo(() => {
    return { page, pageSize }
  }, [page, pageSize])

  const request = useMemo<FetchFeeWithdrawalsPayload>(() => {
    return { status, type, partnerId, limit: pageSize, offset: page * pageSize }
  }, [page, pageSize, partnerId, status, type])

  // The list error is shown inline with Retry, so the toast is silenced.
  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchFeeWithdrawals.request(request), true)
  }, [asyncDispatchWithErrorToast, isAuthenticated, refreshToken, request])

  const handleFiltersChange = useCallback(
    (updates: Partial<FeeWithdrawalFilters>) => {
      const changedParams: Record<string, string | null> = {}

      Object.entries(updates).forEach(([key, value]) => {
        if (value !== filters[key as keyof FeeWithdrawalFilters]) {
          changedParams[key] = value ?? null
        }
      })

      // Re-picking the current value must not rewrite the URL or leave the page.
      if (Object.keys(changedParams).length === 0) {
        return
      }

      updateParams({ ...changedParams, offset: null })
    },
    [filters, updateParams],
  )

  const handleHasReportOnlyChange = useCallback(
    (nextHasReportOnly: boolean) => {
      updateParams({ hasReport: nextHasReportOnly ? 'true' : null, offset: null })
    },
    [updateParams],
  )

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      updateParams(buildFeeWithdrawalsPaginationParams(paginationModel, model))
    },
    [paginationModel, updateParams],
  )

  const handleCloseDetails = useCallback(() => {
    setDetailsWithdrawal(null)
  }, [])

  const previewTitle = report.preview
    ? buildFeeWithdrawalReportFileName(report.preview.withdrawal)
    : ''

  return (
    <div
      data-testid="fees-withdraw-reports-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <PageHeader
        title="Fees Withdraw Reports"
        description="Fee withdrawals from partners, their status and their PDF reports."
      />
      <FeeWithdrawalsToolbar
        filters={filters}
        hasReportOnly={hasReportOnly}
        onFiltersChange={handleFiltersChange}
        onHasReportOnlyChange={handleHasReportOnlyChange}
        onRefresh={handleRefresh}
      />
      {listError ? (
        <div
          css={tw`flex flex-row items-center justify-between gap-4 bg-shades-0 px-4 py-3`}
          data-testid="fee-withdrawals-error"
        >
          <Typography variant="body2" css={tw`text-critical-500`}>
            {listError}
          </Typography>
          <Button
            size="small"
            color="grey"
            onClick={handleRefresh}
            data-testid="fee-withdrawals-retry"
          >
            Retry
          </Button>
        </div>
      ) : (
        <FeeWithdrawalsTable
          hasReportOnly={hasReportOnly}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          onRowClick={setDetailsWithdrawal}
          onDownloadReport={report.downloadReport}
        />
      )}
      <FeeWithdrawalDetailsModal
        withdrawal={detailsWithdrawal}
        isReportLoading={
          detailsWithdrawal !== null && Boolean(report.reportLoadingIds[detailsWithdrawal.id])
        }
        onPreviewReport={report.previewReport}
        onDownloadReport={report.downloadReport}
        onClose={handleCloseDetails}
      />
      <FeeWithdrawalReportPreviewModal
        url={report.preview?.url ?? null}
        title={previewTitle}
        onDownload={report.downloadPreview}
        onClose={report.closePreview}
      />
    </div>
  )
}

export const FeesWithdrawReportsPage = memo(FeesWithdrawReportsPageComponent)
